// Per-visit-type required-field rules.
//
// Single source of truth for both CSV bulk upload, the agency app create-case
// wizard, and the backend createCase() service. Visit-type names match
// visit_types.name verbatim (case-sensitive).
export const SUPPORTED_TASK_TYPES = [
    "SOC",
    "Supervisory Visit",
    "Reassessment visit",
    "Start of Care (Split shift, 2 POC)",
    "Re-assessment (Split shift, 2 POC)",
    "Resumption of Care",
    "Annual Field and Administrative Performance Evaluation"
];
// Types that assess the nurse/aide rather than the patient's plan of care, so
// they carry no certification period, no frequency and no start-of-care date.
const EVALUATION_TASK_TYPES = [
    "Supervisory Visit",
    "Annual Field and Administrative Performance Evaluation"
];
export const SPLIT_SHIFT_WEEKLY_HOURS = 168;
export const SPLIT_SHIFT_VISIT_TYPES = [
    "Start of Care (Split shift, 2 POC)",
    "Re-assessment (Split shift, 2 POC)"
];
export function isSupportedTaskType(name) {
    return SUPPORTED_TASK_TYPES.includes(name);
}
export function isSplitShiftVisitType(name) {
    return SPLIT_SHIFT_VISIT_TYPES.includes(name);
}
export function requiresCertificationDates(taskType) {
    return !EVALUATION_TASK_TYPES.includes(taskType);
}
export function requiresFrequency(taskType) {
    return !EVALUATION_TASK_TYPES.includes(taskType);
}
// "Continuation" visits — patient already has an open episode with a SOC date.
export function requiresStartOfCareDate(taskType) {
    return (taskType === "Reassessment visit" ||
        taskType === "Re-assessment (Split shift, 2 POC)" ||
        taskType === "Resumption of Care");
}
// Returns which fields are required for a given visit-type name. Unknown
// (unsupported) types fall back to all-false — they're not subject to these
// rules. Callers that want to reject unknown types must check
// `isSupportedTaskType` separately.
export function getRequiredFields(visitTypeName) {
    if (!isSupportedTaskType(visitTypeName)) {
        return { certificationDates: false, frequency: false, startOfCareDate: false };
    }
    return {
        certificationDates: requiresCertificationDates(visitTypeName),
        frequency: requiresFrequency(visitTypeName),
        startOfCareDate: requiresStartOfCareDate(visitTypeName)
    };
}
const NUMBER_WORDS = {
    zero: 0,
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
    eleven: 11,
    twelve: 12,
    thirteen: 13,
    fourteen: 14,
    fifteen: 15,
    sixteen: 16,
    seventeen: 17,
    eighteen: 18,
    nineteen: 19,
    twenty: 20,
    "twenty one": 21,
    "twenty-one": 21,
    "twenty two": 22,
    "twenty-two": 22,
    "twenty three": 23,
    "twenty-three": 23,
    "twenty four": 24,
    "twenty-four": 24
};
const NUMBER_SOURCE = "(?:\\d+(?:\\.\\d+)?|twenty[- ]four|twenty[- ]three|twenty[- ]two|twenty[- ]one|twenty|nineteen|eighteen|seventeen|sixteen|fifteen|fourteen|thirteen|twelve|eleven|ten|nine|eight|seven|six|five|four|three|two|one|zero)";
const UNIT_SOURCE = "(?:hours?|hrs?|hr|h|days?|d|shifts?|shift)";
const FORMULA_RE = new RegExp(`(?<a>${NUMBER_SOURCE})\\s*(?<au>${UNIT_SOURCE})?\\s*x\\s*` +
    `(?<b>${NUMBER_SOURCE})\\s*(?<bu>${UNIT_SOURCE})?` +
    `(?:\\s*x\\s*(?<c>${NUMBER_SOURCE})\\s*(?<cu>${UNIT_SOURCE})?)?`, "gi");
function parseFrequencyNumber(raw) {
    if (!raw) {
        return null;
    }
    const normalized = raw.toLowerCase().replace(/\s+/g, " ").trim();
    const wordValue = NUMBER_WORDS[normalized];
    if (wordValue !== undefined) {
        return wordValue;
    }
    const n = Number(normalized);
    return Number.isFinite(n) ? n : null;
}
function normalizeFrequencyText(text) {
    return text
        .toLowerCase()
        .replace(/\u00a0/g, " ")
        .replace(/[\u2013\u2014]/g, "-")
        .replace(/(?<=\d);(?=\d)/g, ":")
        .replace(/[\u00d7*]/g, "x")
        .replace(/\bm\s*-\s*f(?:ri|r)?\b/g, "mon-fri")
        .replace(/\bm\s*-\s*s\b/g, "mon-sun")
        .replace(/(?<=\d)\.\s*(?=days?\b|d\b)/g, " ")
        .replace(/\b(?:multiplied\s+by|times|by)\b/g, "x")
        .replace(/\bper\s+week\b/g, "/week")
        .replace(/\bper\s+day\b/g, "/day")
        .replace(/\s+/g, " ")
        .trim();
}
function hasUnit(unit, pattern) {
    return unit ? pattern.test(unit) : false;
}
function formulaFactors(match) {
    const groups = match.groups ?? {};
    const candidates = [
        { value: parseFrequencyNumber(groups.a), unit: groups.au },
        { value: parseFrequencyNumber(groups.b), unit: groups.bu },
        { value: parseFrequencyNumber(groups.c), unit: groups.cu }
    ];
    return candidates.filter((factor) => factor.value !== null);
}
function parseFormulaTotals(text) {
    const totals = [];
    FORMULA_RE.lastIndex = 0;
    let match;
    while ((match = FORMULA_RE.exec(text)) !== null) {
        let factors = formulaFactors(match);
        const lastFactor = factors.at(-1);
        if (lastFactor &&
            lastFactor.unit === undefined &&
            /^\s*(?:months?|mos?|weeks?|wks?|years?|yrs?)\b/i.test(text.slice(FORMULA_RE.lastIndex))) {
            factors = factors.slice(0, -1);
        }
        const hasHours = factors.some((f) => hasUnit(f.unit, /^h|hour|hr/));
        const hasDays = factors.some((f) => hasUnit(f.unit, /^d|day/));
        const hasShift = factors.some((f) => hasUnit(f.unit, /^shift/));
        const hasAnyUnit = hasHours || hasDays || hasShift;
        const hourFactors = factors.filter((factor) => hasUnit(factor.unit, /^h|hour|hr/));
        const dayFactors = factors.filter((factor) => hasUnit(factor.unit, /^d|day/));
        if (factors.length < 2 ||
            !hasHours ||
            (!hasAnyUnit && factors.length < 3) ||
            factors.some((factor) => factor.unit === undefined && factor.value > 31)) {
            continue;
        }
        if (hourFactors.length > 1 &&
            dayFactors.length === 1 &&
            factors.every((factor) => factor.unit !== undefined)) {
            const dailyHours = hourFactors.reduce((sum, factor) => sum + factor.value, 0);
            totals.push(dailyHours * dayFactors[0].value);
            continue;
        }
        const totalHoursFactor = factors.find((factor) => hasUnit(factor.unit, /^h|hour|hr/) && factor.value > 24);
        if (hasDays && totalHoursFactor) {
            if (totalHoursFactor.value <= SPLIT_SHIFT_WEEKLY_HOURS) {
                totals.push(totalHoursFactor.value);
            }
            continue;
        }
        totals.push(factors.reduce((product, factor) => product * factor.value, 1));
    }
    return totals;
}
function parseHoursDaysTotals(text) {
    const number = NUMBER_SOURCE;
    const hourUnit = "(?:hours?|hrs?|hr|h)";
    const dayUnit = "(?:days?|d)";
    const quantityRe = new RegExp(`(?<value>${number})\\s*(?<unit>${hourUnit}|${dayUnit})(?:\\s*/\\s*(?<per>day|week))?`, "gi");
    const quantities = [];
    let quantityMatch;
    while ((quantityMatch = quantityRe.exec(text)) !== null) {
        const groups = quantityMatch.groups;
        if (!groups) {
            continue;
        }
        const value = parseFrequencyNumber(groups.value);
        if (value === null) {
            continue;
        }
        quantities.push({
            value,
            unit: /^(?:hours?|hrs?|hr|h)$/i.test(groups.unit) ? "hours" : "days",
            per: groups.per === "day" || groups.per === "week" ? groups.per : undefined
        });
    }
    const totals = [];
    let i = 0;
    while (i < quantities.length - 1) {
        const first = quantities[i];
        const second = quantities[i + 1];
        const hours = first.unit === "hours" ? first : second.unit === "hours" ? second : null;
        const days = first.unit === "days" ? first : second.unit === "days" ? second : null;
        if (hours &&
            days &&
            (hours.per === undefined || hours.per === "day") &&
            (days.per === undefined || days.per === "week")) {
            if (hours.value > 24) {
                if (hours.value <= SPLIT_SHIFT_WEEKLY_HOURS) {
                    totals.push(hours.value);
                }
            }
            else {
                totals.push(hours.value * days.value);
            }
            i += 2;
        }
        else {
            i++;
        }
    }
    if (totals.length > 0) {
        return totals;
    }
    const dailyHours = quantities.find((quantity) => quantity.unit === "hours" && quantity.per === "day");
    return dailyHours ? [dailyHours.value * 7] : [];
}
function parseExplicitTotalHours(text) {
    const totalRes = [
        new RegExp(`(?:total|authorized|weekly)\\s+(?:of\\s+)?(?<hours>${NUMBER_SOURCE})\\s*(?:hours?|hrs?|hr|h)`, "i"),
        new RegExp(`(?<hours>${NUMBER_SOURCE})\\s*(?:hours?|hrs?|hr|h)\\s*(?:/week|(?:a|per)\\s+week|weekly)\\b`, "i"),
        new RegExp(`(?<hours>${NUMBER_SOURCE})\\s*(?:x|/)\\s*(?:week|wk)\\b`, "i"),
        new RegExp(`(?<hours>${NUMBER_SOURCE})\\s*/week\\b`, "i")
    ];
    for (const totalRe of totalRes) {
        const match = totalRe.exec(text);
        if (match?.groups) {
            return parseFrequencyNumber(match.groups.hours);
        }
    }
    return null;
}
function parseDayNightSplitTotal(text) {
    const dayCountMatch = /\b(?<days>\d+(?:\.\d+)?)\s*(?:days?|d)(?:\/week)?\b/i.exec(text);
    if (!dayCountMatch?.groups) {
        return null;
    }
    const days = Number(dayCountMatch.groups.days);
    if (!Number.isFinite(days) || days <= 0 || days > 7) {
        return null;
    }
    const dayThenNight = /\b12\s*(?:hours?|hrs?|hr|h)?\s*(?:day|days?|daytime)\b.{0,24}\b(?:12\s*(?:hours?|hrs?|hr|h)?\s*)?(?:night|nights?|overnight)\b/i;
    const nightThenDay = /\b12\s*(?:hours?|hrs?|hr|h)?\s*(?:night|nights?|overnight)\b.{0,24}\b(?:12\s*(?:hours?|hrs?|hr|h)?\s*)?(?:day|days?|daytime)\b/i;
    if (!dayThenNight.test(text) && !nightThenDay.test(text)) {
        return null;
    }
    return days * 24;
}
function formatHours(value) {
    if (Math.abs(value - Math.round(value)) < 0.01) {
        return String(Math.round(value));
    }
    return value.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}
const SCHEDULE_DAY_INDEX = new Map([
    ["sun", 0],
    ["sunday", 0],
    ["sundays", 0],
    ["mon", 1],
    ["monday", 1],
    ["mondays", 1],
    ["tue", 2],
    ["tues", 2],
    ["tuesday", 2],
    ["tuesdays", 2],
    ["wed", 3],
    ["weds", 3],
    ["wednesday", 3],
    ["wednesdays", 3],
    ["thu", 4],
    ["thur", 4],
    ["thurs", 4],
    ["thursday", 4],
    ["thursdays", 4],
    ["fri", 5],
    ["friday", 5],
    ["fridays", 5],
    ["sat", 6],
    ["saturday", 6],
    ["saturdays", 6]
]);
const SCHEDULE_DAY_NAME = "(?:sun(?:day|days)?|mon(?:day|days)?|tues?(?:day|days)?|wed(?:nesday|nesdays|s)?|thu(?:rs?|rsday|rsdays)?|thur|fri(?:day|days)?|sat(?:urday|urdays)?)";
const SCHEDULE_TIME_RANGE_RE = /\b(?<start>\d{1,2}(?::\d{2})?\s*(?:am|pm|a|p)?)\s*(?:-|to|until|till)\s*(?<end>\d{1,2}(?::\d{2})?\s*(?:am|pm|a|p)?)\b/gi;
function scheduleDayRange(start, end) {
    const days = [start];
    let cursor = start;
    while (cursor !== end) {
        cursor = (cursor + 1) % 7;
        days.push(cursor);
        if (days.length === 7) {
            break;
        }
    }
    return days;
}
function scheduleDayTokenToIndex(token) {
    return SCHEDULE_DAY_INDEX.get(token.trim().toLowerCase());
}
function orderedUniqueNumbers(values) {
    const seen = new Set();
    const out = [];
    for (const value of values) {
        if (!seen.has(value)) {
            seen.add(value);
            out.push(value);
        }
    }
    return out;
}
function scheduleDaysFromExpression(expr) {
    const days = [];
    const rangeRe = new RegExp(`\\b(?<start>${SCHEDULE_DAY_NAME})\\s*(?:-|to|through)\\s*(?<end>${SCHEDULE_DAY_NAME})\\b`, "gi");
    const consumed = [];
    for (const match of expr.matchAll(rangeRe)) {
        const start = scheduleDayTokenToIndex(match.groups?.start ?? "");
        const end = scheduleDayTokenToIndex(match.groups?.end ?? "");
        if (start !== undefined && end !== undefined) {
            days.push(...scheduleDayRange(start, end));
            consumed.push([match.index ?? 0, (match.index ?? 0) + match[0].length]);
        }
    }
    const chars = [...expr];
    for (const [start, end] of consumed) {
        for (let idx = start; idx < end; idx++) {
            chars[idx] = " ";
        }
    }
    const dayRe = new RegExp(`\\b${SCHEDULE_DAY_NAME}\\b`, "gi");
    for (const match of chars.join("").matchAll(dayRe)) {
        const day = scheduleDayTokenToIndex(match[0]);
        if (day !== undefined) {
            days.push(day);
        }
    }
    return orderedUniqueNumbers(days);
}
function isBareScheduleDayList(text) {
    if (!text.trim() || /\d|\bnone\b/.test(text) || scheduleDaysFromExpression(text).length === 0) {
        return false;
    }
    const dayRe = new RegExp(`\\b${SCHEDULE_DAY_NAME}\\b`, "gi");
    const remainder = text
        .replace(dayRe, " ")
        .replace(/\b(?:and|or|to|through)\b/g, " ")
        .replace(/[-/&,\s]+/g, "");
    return remainder.length === 0;
}
function schedulePrefixContext(text, start, end) {
    let snippet = text.slice(start, end).split(/[.;]/).at(-1) ?? "";
    const commaParts = snippet.split(",");
    if (commaParts.length > 1) {
        const keep = [commaParts[commaParts.length - 1]];
        for (let idx = commaParts.length - 2; idx >= 0; idx--) {
            const part = commaParts[idx];
            if (!isBareScheduleDayList(part)) {
                break;
            }
            keep.unshift(part);
        }
        snippet = keep.join(",");
    }
    return snippet.slice(-120);
}
function scheduleSuffixContext(text, start, end) {
    return (text.slice(start, end).split(/[.;,]/)[0] ?? "").slice(0, 120);
}
function parseScheduleTime(raw) {
    const match = /^\s*(?<hour>\d{1,2})(?::(?<minute>\d{2}))?\s*(?<ampm>am|pm|a|p)?\s*$/i.exec(raw);
    if (!match?.groups) {
        return null;
    }
    const rawHour = Number(match.groups.hour);
    const minute = match.groups.minute ? Number(match.groups.minute) : 0;
    if (rawHour > 24 || minute > 59) {
        return null;
    }
    const ampm = match.groups.ampm?.toLowerCase();
    let hour = rawHour;
    if (ampm === "pm" || ampm === "p") {
        hour = rawHour === 12 ? 12 : rawHour + 12;
    }
    else if (ampm === "am" || ampm === "a") {
        hour = rawHour === 12 ? 0 : rawHour;
    }
    return { minutes: hour * 60 + minute, rawHour, explicitAmpm: ampm !== undefined };
}
function scheduleRangeHours(startRaw, endRaw) {
    const start = parseScheduleTime(startRaw);
    const end = parseScheduleTime(endRaw);
    if (!start || !end) {
        return null;
    }
    let startMinutes = start.minutes;
    let endMinutes = end.minutes;
    if (!start.explicitAmpm && end.explicitAmpm && /p/i.test(endRaw) && start.rawHour <= 12) {
        startMinutes = (start.rawHour % 12) * 60 + 12 * 60;
    }
    if (!start.explicitAmpm &&
        !end.explicitAmpm &&
        start.rawHour >= 8 &&
        end.rawHour >= 1 &&
        end.rawHour <= 7) {
        endMinutes += 12 * 60;
    }
    else if (!start.explicitAmpm &&
        !end.explicitAmpm &&
        endMinutes <= startMinutes &&
        start.rawHour <= 12 &&
        end.rawHour <= 12) {
        endMinutes += 12 * 60;
    }
    if (endMinutes <= startMinutes) {
        endMinutes += 24 * 60;
    }
    const hours = (endMinutes - startMinutes) / 60;
    return hours > 0 && hours <= 24 ? hours : null;
}
function parseScheduleTotalHours(text) {
    const hasScheduleDay = new RegExp(`\\b${SCHEDULE_DAY_NAME}\\b`, "i").test(text);
    const dayCount = parseScheduleDayCount(text);
    if (!hasScheduleDay && dayCount === null) {
        return null;
    }
    const ranges = [...text.matchAll(SCHEDULE_TIME_RANGE_RE)]
        .map((match) => {
        const hours = scheduleRangeHours(match.groups?.start ?? "", match.groups?.end ?? "");
        return hours === null
            ? null
            : {
                hours,
                span: [match.index ?? 0, (match.index ?? 0) + match[0].length]
            };
    })
        .filter((range) => range !== null);
    if (ranges.length === 0) {
        return null;
    }
    if (!hasScheduleDay && dayCount !== null) {
        const dailyHours = ranges.reduce((sum, range) => sum + range.hours, 0);
        return Math.min(24, dailyHours) * dayCount;
    }
    const dayHours = new Map();
    let previousDays = [];
    let previousEnd = 0;
    ranges.forEach((range, idx) => {
        const nextStart = idx + 1 < ranges.length ? ranges[idx + 1].span[0] : text.length;
        const prefix = schedulePrefixContext(text, previousEnd, range.span[0]);
        const suffix = scheduleSuffixContext(text, range.span[1], Math.min(nextStart, range.span[1] + 100));
        const prefixDays = scheduleDaysFromExpression(prefix);
        const suffixDays = scheduleDaysFromExpression(suffix);
        let days = prefixDays;
        if (suffixDays.length > 0 && (days.length === 0 || /(?:\b(?:and|or)|,)\s*$/.test(prefix))) {
            days = suffixDays;
        }
        if (days.length === 0 && previousDays.length > 0) {
            const between = text.slice(previousEnd, range.span[0]);
            if (between.trim().length <= 12 || /^\s*(?:,|and)?\s*$/.test(between)) {
                days = previousDays;
            }
        }
        for (const day of days) {
            dayHours.set(day, (dayHours.get(day) ?? 0) + range.hours);
        }
        if (days.length > 0) {
            previousDays = days;
        }
        previousEnd = range.span[1];
    });
    if (dayHours.size === 0) {
        return null;
    }
    return [...dayHours.values()].reduce((sum, hours) => sum + Math.min(24, hours), 0);
}
function parseScheduleDayCount(text) {
    const match = /\b(?<days>\d+(?:\.\d+)?)\s*d(?:ays?)?\s*x?\b/i.exec(text);
    if (!match?.groups) {
        return null;
    }
    const days = Number(match.groups.days);
    return Number.isFinite(days) && days > 0 && days <= 7 ? days : null;
}
export function parseVisitFrequencyHours(text) {
    const normalized = normalizeFrequencyText(text ?? "");
    if (!normalized) {
        return { totalHoursPerWeek: null, rule: "unparsed" };
    }
    if (/\b(?:live\s*-?\s*in|livein)\b/.test(normalized) ||
        (/\b24\s*(?:hours?|hrs?|hr|h)\b/.test(normalized) &&
            /\b(?:care|scare)\b/.test(normalized) &&
            !/\bsplit\b/.test(normalized))) {
        return { totalHoursPerWeek: 91, rule: "live-in-care" };
    }
    if (/\b24\s*(?:\/|-)\s*7\b/.test(normalized) ||
        /\b(?:around|round)\s+the\s+clock\b/.test(normalized)) {
        return { totalHoursPerWeek: SPLIT_SHIFT_WEEKLY_HOURS, rule: "twenty-four-seven" };
    }
    if (/\b(?:7\s*x\s*24|24\s*x\s*7)\b/.test(normalized) && /\bsplit\b/.test(normalized)) {
        return { totalHoursPerWeek: SPLIT_SHIFT_WEEKLY_HOURS, rule: "twenty-four-seven" };
    }
    const dayNightSplitTotal = parseDayNightSplitTotal(normalized);
    if (dayNightSplitTotal !== null) {
        return { totalHoursPerWeek: dayNightSplitTotal, rule: "formula" };
    }
    const explicitTotal = parseExplicitTotalHours(normalized);
    if (explicitTotal !== null) {
        return { totalHoursPerWeek: explicitTotal, rule: "total-hours" };
    }
    const formulaTotals = parseFormulaTotals(normalized);
    if (formulaTotals.length > 0) {
        return {
            totalHoursPerWeek: formulaTotals.reduce((sum, total) => sum + total, 0),
            rule: "formula"
        };
    }
    const hoursDaysTotals = parseHoursDaysTotals(normalized);
    if (hoursDaysTotals.length > 0) {
        return {
            totalHoursPerWeek: hoursDaysTotals.reduce((sum, total) => sum + total, 0),
            rule: "hours-days"
        };
    }
    const scheduleTotal = parseScheduleTotalHours(normalized);
    if (scheduleTotal !== null) {
        return { totalHoursPerWeek: scheduleTotal, rule: "schedule" };
    }
    if (/\bsplit\b/.test(normalized) && /\b24\s*(?:hours?|hrs?|hr|h)\b/.test(normalized)) {
        return { totalHoursPerWeek: SPLIT_SHIFT_WEEKLY_HOURS, rule: "twenty-four-seven" };
    }
    return { totalHoursPerWeek: null, rule: "unparsed" };
}
// ADVISORY ONLY — never block on this.
//
// Frequency is free text that ends up on the Patient Assessment, Plan of Care
// and CMS-485 as the caregiver frequency; it is not a staffing input. Agencies
// legitimately send split-shift cases whose written frequency reads as less
// than 168 h/week (partial weeks, a day the family covers, contract text copied
// verbatim from the payer). Blocking those left real cases un-broadcastable
// with no override (Platinum, Jul 2026: three RAs stuck for three days).
//
// So this returns a message to SHOW, not an error to enforce. Callers render it
// next to the field and let the user proceed. A blank frequency returns null —
// that is a required-field concern, owned by each caller's own required checks.
export function getSplitShiftFrequencyWarning(visitTypeName, frequency) {
    if (!isSplitShiftVisitType(visitTypeName) || !frequency?.trim()) {
        return null;
    }
    const parsed = parseVisitFrequencyHours(frequency);
    if (parsed.totalHoursPerWeek === null) {
        return ("Frequency doesn't clearly show 24/7 split-shift coverage. " +
            "Split shift is normally 168 hours/week, so double-check this text.");
    }
    if (Math.abs(parsed.totalHoursPerWeek - SPLIT_SHIFT_WEEKLY_HOURS) > 0.01) {
        return (`Frequency reads as ${formatHours(parsed.totalHoursPerWeek)} hours/week, ` +
            "but split shift is normally 168 hours/week. Double-check this text.");
    }
    return null;
}
//# sourceMappingURL=visit-type-rules.js.map